import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { ClockCounterClockwise, ArrowLeft, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const CSV_HEADERS = [
  "Deleted at", "Reason", "Note", "Actor name", "Actor email",
  "Amount (INR)", "Creator", "Brand", "Campaign",
  "Paid at", "Payment type", "Mode", "Reference",
  "Razorpay payout id", "Simulated", "Payment id",
];

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const csv = [CSV_HEADERS.join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const REASON_TONE = {
  "Mistake": "bg-rose-50 text-rose-700 border-rose-200",
  "Duplicate": "bg-amber-50 text-amber-700 border-amber-200",
  "Wrong amount": "bg-orange-50 text-orange-700 border-orange-200",
  "Refunded": "bg-blue-50 text-blue-700 border-blue-200",
  "Other": "bg-slate-100 text-slate-700 border-slate-300",
};

export default function AuditLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-log/payments", { params: { limit: 200 } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load audit log");
      if (e.response?.status === 403) navigate("/");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, load, navigate]);

  const filtered = items.filter((row) => {
    if (reasonFilter !== "all" && row.reason !== reasonFilter) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    const hay = [
      row.reason, row.note, row.actor_name, row.actor_email,
      row.snapshot?.creator_username, row.snapshot?.brand_name,
      row.snapshot?.campaign_name, row.snapshot?.reference,
      String(row.snapshot?.amount || ""),
    ].filter(Boolean).map(String).join(" ").toLowerCase();
    return hay.includes(s);
  });

  const REASONS = ["Mistake", "Duplicate", "Wrong amount", "Refunded", "Other"];

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/users" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back to Team
          </Link>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Compliance</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <ClockCounterClockwise size={28} weight="duotone" className="text-blue-600" />
            Payment audit log
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Every payment deletion with reason, note, actor and full snapshot. Newest first · admin-only.
          </p>
        </div>
        <Button
          onClick={() => {
            const rows = filtered.map((row) => {
              const s = row.snapshot || {};
              return [
                row.created_at, row.reason, row.note,
                row.actor_name, row.actor_email,
                s.amount, s.creator_username, s.brand_name, s.campaign_name,
                s.paid_at, s.payment_type, s.mode, s.reference,
                s.razorpay_payout_id, s.razorpay_simulation ? "Yes" : "No",
                row.entity_id,
              ];
            });
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCsv(rows, `payment-audit-log-${stamp}.csv`);
          }}
          disabled={filtered.length === 0}
          variant="outline"
          className="border-slate-300 shrink-0"
          data-testid="audit-export-csv-btn"
        >
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV ({filtered.length})
        </Button>
      </div>

      <Card className="p-3 flex flex-wrap gap-2 items-center border border-slate-200 shadow-none">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by creator, brand, actor, note, reference…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={reasonFilter} onValueChange={setReasonFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="audit-reason-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reasons</SelectItem>
            {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">
          {filtered.length} of {items.length} entries
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Loading audit log…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border border-dashed border-slate-300 shadow-none">
          <ClockCounterClockwise size={32} className="mx-auto text-slate-300 mb-2" weight="duotone" />
          <div className="text-sm text-slate-500">
            {items.length === 0 ? "No payment deletions logged yet. When an admin deletes a payment, it lands here." : "No entries match your filters."}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const s = row.snapshot || {};
            return (
              <Card key={row.id} className="p-4 sm:p-5 border border-slate-200 shadow-none" data-testid={`audit-row-${row.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="stat-num text-lg font-bold text-slate-900">{fmtCur(s.amount)}</span>
                      <Badge variant="outline" className={REASON_TONE[row.reason] || "bg-slate-100 text-slate-700"}>
                        {row.reason}
                      </Badge>
                      {s.razorpay_simulation && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">SIM</Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-700 mt-1">
                      <span className="text-slate-500">Paid to</span>{" "}
                      <b>@{s.creator_username || "—"}</b>
                      {" · "}
                      <span className="text-slate-500">for</span>{" "}
                      <b>{s.brand_name || "—"}</b>
                      {s.campaign_name && <span className="text-slate-500"> ({s.campaign_name})</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                      Paid: {fmtDateTime(s.paid_at)} · {s.mode || "—"}{s.reference ? ` · ref ${s.reference}` : ""}
                    </div>
                    {row.note && (
                      <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-700 whitespace-pre-wrap">
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] mr-2">Note</span>
                        {row.note}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 sm:text-right shrink-0 sm:min-w-[180px]">
                    <div className="font-medium text-slate-900">{row.actor_name || "—"}</div>
                    <div className="truncate">{row.actor_email || ""}</div>
                    <div className="font-mono mt-1">{fmtDateTime(row.created_at)}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
